/**
 * useTiledContent Hook
 *
 * 职责: 管理平铺(tiled)模式的渲染逻辑
 * 用useMemo代替state存储方法引用,避免闭包陷阱
 */

import React, { useMemo, useCallback } from 'react';
import Icon from '../../../wui-icon/src';
import Tabs from '../../../wui-tabs/src';
import type { CascaderOption, FieldNames } from '../iCascader';

const { TabPane } = Tabs;

interface UseTiledContentProps {
    // 显示控制
    showSearch: boolean;
    inputValue: string;

    // 平铺模式数据
    tabsArr: Array<{
        tab: string;
        key: string;
        content: CascaderOption[];
    }>;
    tabsActiveKey: string;

    // 样式与配置
    prefixCls: string;
    fieldNames?: FieldNames;
    loadDataFlag?: boolean;

    // 自定义渲染
    dropdownTabsContent?: (val: CascaderOption[]) => {
        dropdownTabsContentTop?: React.ReactNode;
        dropdownTabsContentBody?: React.ReactNode;
    };

    // DOM引用
    input?: { input?: { offsetWidth?: number } };

    // 回调函数
    onSearchItemClick: (item: any) => void;
    onChangeTabs: (val: string) => void;
    onChangeValue: (val: CascaderOption) => void;

    // 搜索过滤数据
    generateFilteredOptions: (prefix: string) => CascaderOption[];
}

/**
 * 搜索结果单项组件
 */
const SearchResultItem: React.FC<{
    item: any;
    fieldNames?: FieldNames;
    prefixCls: string;
    onClick: (item: any) => void;
}> = React.memo(({ item, fieldNames, prefixCls, onClick }) => {
    const labelKey = fieldNames?.label || 'label';

    if (!item?.path) return null;

    const titleText = item.path.map((labelItem: any) => labelItem[labelKey]).join(' / ');
    const displayText = item.path.map((labelItem: any, j: number) => {
        return labelItem[labelKey] + `${j + 1 === item.path.length ? '' : ' / '}`;
    }).join('');

    return (
        <div
            className={`${prefixCls}-tiled-search-item`}
            onClick={() => onClick(item)}
            title={titleText}
        >
            {displayText}
        </div>
    );
});

SearchResultItem.displayName = 'SearchResultItem';

/**
 * Tab标题组件
 */
const TabTitle: React.FC<{
    item: { tab: string; key: string };
    activeKey: string;
}> = React.memo(({ item, activeKey }) => {
    return (
        <span>
            {item.tab}
            <Icon
                type="uf-arrow-down"
                rotate={item.key === activeKey ? 180 : 0}
            />
        </span>
    );
});

TabTitle.displayName = 'TabTitle';

/**
 * Tab内容组件
 */
const TabContent: React.FC<{
    item: { content: CascaderOption[] };
    prefixCls: string;
    fieldNames?: FieldNames;
    dropdownTabsContent?: (val: CascaderOption[]) => {
        dropdownTabsContentTop?: React.ReactNode;
        dropdownTabsContentBody?: React.ReactNode;
    };
    onChangeValue: (val: CascaderOption) => void;
}> = React.memo(({ item, prefixCls, fieldNames, dropdownTabsContent, onChangeValue }) => {
    const valueKey = fieldNames?.value || 'value';
    const labelKey = fieldNames?.label || 'label';

    return (
        <>
            {dropdownTabsContent?.(item.content)?.dropdownTabsContentTop}
            <div className={`${prefixCls}-tiled-items`}>
                {dropdownTabsContent ? (
                    dropdownTabsContent(item.content)?.dropdownTabsContentBody
                ) : (
                    item.content?.map((optionItem: any) => (
                        <div
                            className={`${prefixCls}-tiled-item`}
                            key={optionItem[valueKey]}
                            onClick={() => onChangeValue(optionItem)}
                            title={optionItem[labelKey]}
                        >
                            {optionItem[labelKey]}
                        </div>
                    ))
                )}
            </div>
        </>
    );
});

TabContent.displayName = 'TabContent';

/**
 * 主Hook
 */
export const useTiledContent = (props: UseTiledContentProps) => {
    const {
        showSearch,
        inputValue,
        tabsArr,
        tabsActiveKey,
        prefixCls,
        fieldNames,
        loadDataFlag,
        dropdownTabsContent,
        input,
        onSearchItemClick,
        onChangeTabs,
        onChangeValue,
        generateFilteredOptions,
    } = props;

    /**
     * 渲染搜索盒子
     *
     * 当showSearch=true且inputValue不为空时显示
     */
    const renderSearchBox = useCallback(() => {
        const searchData = generateFilteredOptions('');
        const inputWidth = input?.input?.offsetWidth;

        // 如果正在加载,显示空盒子
        if (loadDataFlag) {
            return (
                <div
                    className={`${prefixCls}-tiled-search-box`}
                    style={{ width: inputWidth ? `${inputWidth}px` : undefined }}
                />
            );
        }

        return (
            <div
                className={`${prefixCls}-tiled-search-box`}
                style={{ width: inputWidth ? `${inputWidth}px` : undefined }}
            >
                {searchData.map((item: any, index: number) => (
                    <SearchResultItem
                        key={index}
                        item={item}
                        fieldNames={fieldNames}
                        prefixCls={prefixCls}
                        onClick={onSearchItemClick}
                    />
                ))}
            </div>
        );
    }, [
        generateFilteredOptions,
        input,
        loadDataFlag,
        prefixCls,
        fieldNames,
        onSearchItemClick,
    ]);

    /**
     * 渲染平铺Tabs
     *
     * 默认显示的tabs层级导航视图
     */
    const renderTiledTabs = useCallback(() => {
        return (
            <div className={`${prefixCls}-tiled-tabs`}>
                <Tabs
                    activeKey={tabsActiveKey}
                    onChange={onChangeTabs}
                    type="card"
                    moreType="moreTabsArrow"
                >
                    {tabsArr?.map(item => (
                        <TabPane
                            key={item.key}
                            tab={<TabTitle item={item} activeKey={tabsActiveKey} />}
                        >
                            <TabContent
                                item={item}
                                prefixCls={prefixCls}
                                fieldNames={fieldNames}
                                dropdownTabsContent={dropdownTabsContent}
                                onChangeValue={onChangeValue}
                            />
                        </TabPane>
                    ))}
                </Tabs>
            </div>
        );
    }, [
        prefixCls,
        tabsArr,
        tabsActiveKey,
        onChangeTabs,
        fieldNames,
        dropdownTabsContent,
        onChangeValue,
    ]);

    /**
     * 最终返回的渲染内容
     *
     * 根据showSearch和inputValue决定显示搜索盒子还是tabs
     * 使用useMemo确保只在依赖变化时重新计算
     */
    const tiledContent = useMemo(() => {
        // 搜索模式: 有搜索内容时显示搜索结果
        if (showSearch && inputValue !== '') {
            return renderSearchBox();
        }

        // 正常模式: 显示tabs层级导航
        return renderTiledTabs();
    }, [
        showSearch,
        inputValue,
        renderSearchBox,
        renderTiledTabs,
    ]);

    return tiledContent;
};
